import { forwardRef } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Filter pill — clickable, toggleable, optional count, optional tone.
 *
 * Replaces the IssuesFilterStrip's local `Chip`, the inbox's status /
 * mailbox / category chips, and the saved-views inline pill that some
 * pages built ad-hoc. Same interaction semantics everywhere:
 *
 *   click active     → clears the filter (parent decides; we just call onClick)
 *   click inactive   → activates the filter
 *   count === 0      → disabled, dimmed, no hover
 *
 * `tone` paints both the count and the active background so the
 * critical / warning / muted vocabulary lines up with `StatusBadge`.
 */
export type FilterChipTone = "neutral" | "critical" | "warning" | "info" | "success" | "muted";

const TONE_COUNT_CLS: Record<FilterChipTone, string> = {
  neutral:  "text-fg-bright",
  critical: "text-status-critical",
  warning:  "text-status-medium",
  info:     "text-status-info",
  success:  "text-status-low",
  muted:    "text-fg-dim",
};

const TONE_ACTIVE_CLS: Record<FilterChipTone, string> = {
  neutral:  "bg-bg-elev ring-1 ring-line-strong",
  critical: "bg-status-critical/10 ring-1 ring-status-critical/40",
  warning:  "bg-status-medium/10 ring-1 ring-status-medium/40",
  info:     "bg-status-info/10 ring-1 ring-status-info/40",
  success:  "bg-status-low/10 ring-1 ring-status-low/40",
  muted:    "bg-bg-elev ring-1 ring-line-strong",
};

export type FilterChipProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  label: React.ReactNode;
  count?: number;
  tone?: FilterChipTone;
  active?: boolean;
  icon?: LucideIcon;
  /** When true, dim and disable when `count === 0`. Defaults to true. */
  disableWhenEmpty?: boolean;
};

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(function FilterChip(
  {
    label,
    count,
    tone = "neutral",
    active = false,
    icon: Icon,
    disableWhenEmpty = true,
    className,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  const isEmpty = typeof count === "number" && count === 0;
  const isDisabled = disabled || (disableWhenEmpty && isEmpty);
  return (
    <button
      ref={ref}
      type={type}
      aria-pressed={active}
      disabled={isDisabled}
      className={cn(
        "inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[12px] transition focus-ring",
        "hover:bg-bg-elev",
        active && TONE_ACTIVE_CLS[tone],
        isDisabled && "opacity-50 cursor-not-allowed hover:bg-transparent",
        className,
      )}
      {...rest}
    >
      {Icon && <Icon size={11} className={active ? "text-fg" : "text-fg-muted"} />}
      <span className={cn(active ? "text-fg-bright" : "text-fg-dim")}>{label}</span>
      {typeof count === "number" && (
        <span className={cn("tabular-nums text-[12px] font-medium", TONE_COUNT_CLS[tone])}>
          {count}
        </span>
      )}
    </button>
  );
});
